import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import PropTypes from "prop-types"
import {Stack} from "@mui/material"
import {RoutineService} from "src/services/routine-service"
import {dispatchSnackbar} from "src/v2/components/SnackBar/snackbar-bus"
import WorkflowExecutionDrawer from "src/v2/components/Workflow/workflow-execution-drawer"
import WorkflowExecutionFilters from "src/v2/components/Workflow/workflow-execution-filters"
import WorkflowExecutionsTable from "src/v2/components/Workflow/workflow-executions-table"
import RoutineExecutionForm from "src/v2/components/Workflow/routine-execution-form"
import {
    normalizeWorkflowExecution,
    shouldPollWorkflows,
} from "src/v2/components/Workflow/workflow-execution"
import usePaginatedExecutions from "../hooks/use-paginated-executions"
import {
    executeWorkflowRoutine,
    getRoutineErrorMessage,
    workflowRoutineDefinitions,
} from "../routine-definitions"

const INITIAL_FILTERS = {
    dagId: "",
    status: "",
    contractVersion: "",
    periodStart: "",
    periodEnd: "",
}

const WorkflowsTab = ({active, onLoadingChange}) => {
    const service = useMemo(() => new RoutineService(), [])
    const [filters, setFilters] = useState(INITIAL_FILTERS)
    const [actionLoading, setActionLoading] = useState(false)
    const [selectedExecutionId, setSelectedExecutionId] = useState(null)
    const [selectedExecution, setSelectedExecution] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const detailSequence = useRef(0)
    const lastLoadError = useRef(null)
    const submitting = useRef(false)

    const fetchPage = useCallback(
        ({page, pageSize}) => service.listWorkflowExecutions({
            page,
            pageSize,
            ...filters,
        }),
        [filters, service]
    )

    const executions = usePaginatedExecutions({
        fetchPage,
        normalizeItem: normalizeWorkflowExecution,
        active,
    })

    useEffect(() => {
        const message = executions.error ? "Erro ao carregar execuções de workflows" : null
        if (message && message !== lastLoadError.current) dispatchSnackbar(message, "error")
        lastLoadError.current = message
    }, [executions.error])

    useEffect(() => () => onLoadingChange(false), [onLoadingChange])

    const loadDetail = useCallback(async (executionId, {silent = false} = {}) => {
        const requestId = ++detailSequence.current
        if (!silent) setDetailLoading(true)
        try {
            const response = await service.getRoutineWorkflowExecution(executionId)
            if (requestId !== detailSequence.current) return
            setSelectedExecution(normalizeWorkflowExecution(response?.body ?? response?.data ?? {}))
        } catch (_) {
            if (requestId === detailSequence.current && !silent) {
                dispatchSnackbar("Não foi possível carregar os detalhes da execução", "error")
            }
        } finally {
            if (requestId === detailSequence.current && !silent) setDetailLoading(false)
        }
    }, [service])

    const openExecution = (execution) => {
        setSelectedExecutionId(execution.id)
        setSelectedExecution(execution)
        loadDetail(execution.id)
    }

    const closeExecution = () => {
        detailSequence.current += 1
        setSelectedExecutionId(null)
        setSelectedExecution(null)
        setDetailLoading(false)
    }

    useEffect(() => {
        const drawerOpen = selectedExecutionId !== null
        if (!active || !shouldPollWorkflows(executions.rows, drawerOpen, document.visibilityState)) {
            return undefined
        }

        const refresh = () => {
            if (document.visibilityState !== "visible") return
            executions.load({silent: true})
            if (selectedExecutionId !== null) loadDetail(selectedExecutionId, {silent: true})
        }
        const interval = window.setInterval(refresh, 5000)
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") refresh()
        }
        document.addEventListener("visibilitychange", onVisibilityChange)
        return () => {
            window.clearInterval(interval)
            document.removeEventListener("visibilitychange", onVisibilityChange)
        }
    }, [active, executions.load, executions.rows, loadDetail, selectedExecutionId])

    const runAction = async (action, successMessage) => {
        if (submitting.current) return
        submitting.current = true
        setActionLoading(true)
        onLoadingChange(true)
        try {
            const response = await action()
            dispatchSnackbar(typeof successMessage === "function" ? successMessage(response) : successMessage, "success")
            await executions.load({silent: true})
        } catch (error) {
            dispatchSnackbar(getRoutineErrorMessage(error), "error")
        } finally {
            submitting.current = false
            setActionLoading(false)
            onLoadingChange(false)
        }
    }

    const submit = (definition, values) => runAction(
        () => executeWorkflowRoutine(service, definition, values),
        definition.successMessage
    )

    const applyFilters = (nextFilters) => {
        setFilters(nextFilters)
        executions.resetPage()
    }

    return (
        <Stack spacing={3} sx={{pt: 3}}>
            <RoutineExecutionForm
                routines={workflowRoutineDefinitions}
                loading={actionLoading}
                onSubmit={submit}
                onRebuild={() => runAction(() => service.startRebuild(), "Rebuild index iniciado")}
                onValidationError={(message) => dispatchSnackbar(message, "warning")}
            />
            <WorkflowExecutionFilters
                value={filters}
                loading={executions.loading}
                onApply={applyFilters}
            />
            <WorkflowExecutionsTable
                rows={executions.rows}
                total={executions.total}
                page={executions.page}
                pageSize={executions.pageSize}
                loading={executions.loading}
                resetKey={JSON.stringify(filters)}
                onPageChange={executions.changePage}
                onOpenExecution={openExecution}
                onRefresh={() => executions.load()}
            />
            <WorkflowExecutionDrawer
                open={selectedExecutionId !== null}
                execution={selectedExecution}
                loading={detailLoading}
                onClose={closeExecution}
            />
        </Stack>
    )
}

WorkflowsTab.propTypes = {
    active: PropTypes.bool,
    onLoadingChange: PropTypes.func.isRequired,
}

export default WorkflowsTab
